import { useSyncExternalStore } from 'react';
import { loadPrefs, savePrefs, PREFS_KEY } from '../lib/user-prefs';

// Demo mode (Cape Cod sample data) is a persisted preference with a tiny module-level store so
// the account menu, the map ribbon and MapView all update live without a provider. Other tabs
// write the same localStorage key; their `storage` events resync this tab.
const stateListeners = new Set();
const changeListeners = new Set();

function onStorage(e) {
  if (e && e.key !== null && e.key !== PREFS_KEY) return; // key null = storage cleared
  stateListeners.forEach((fn) => fn());
}

function subscribe(fn) {
  stateListeners.add(fn);
  if (stateListeners.size === 1 && typeof window !== 'undefined') window.addEventListener('storage', onStorage);
  return () => {
    stateListeners.delete(fn);
    if (stateListeners.size === 0 && typeof window !== 'undefined') window.removeEventListener('storage', onStorage);
  };
}

// The project open on the map (set by MapView), so the toast can say when nothing will show.
let contextProject = null;
export function setDemoContextProject(project) { contextProject = project || null; }

/** Toast text for a demo switch: says when the sample data will not appear on the open project. */
export function demoToastMessage(on, project = contextProject) {
  if (!on) return 'Sample data hidden';
  if (project && !isDemoEnabled(true, project)) {
    return 'Demo mode on. Sample data appears when no project is open (or on the Cape Cod project)';
  }
  return 'Showing sample Cape Cod data';
}

export function getDemoMode() {
  return loadPrefs().demoMode === true;
}

export function setDemoMode(value) {
  const next = value === true;
  if (next === getDemoMode()) return;
  savePrefs({ ...loadPrefs(), demoMode: next });
  stateListeners.forEach((fn) => fn());
  changeListeners.forEach((fn) => fn(next));
}

/** Called only when the user flips the switch (not on mount); used for the toast. */
export function onDemoModeChanged(fn) {
  changeListeners.add(fn);
  return () => changeListeners.delete(fn);
}

export function useDemoMode() {
  const demoMode = useSyncExternalStore(subscribe, getDemoMode, () => false);
  return [demoMode, setDemoMode];
}

/** Whether the Cape Cod sample data may be listed, fetched and rendered. */
export function isDemoEnabled(demoMode, currentProject) {
  return !!demoMode && (!currentProject || !!currentProject.location?.toLowerCase().includes('cape cod'));
}
